import React, { useState } from "react";
import { Theme, Menu, MenuItem, Fade, ListItemIcon, ListItemText, Divider, Typography, List, Popover, makeStyles } from '@material-ui/core';

import FolderIcon from '@material-ui/icons/Folder';
import SermonSeriesIcon from '@material-ui/icons/LibraryBooks';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import classNames from 'classnames';
import { useOfflineAssets } from "../../../offline/useOfflineAssets";
import { AssetCategory } from "../../../../entities/AssetCategory";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { AssetCategoryType, AssetCategoryTypes } from "../../../../entities/AssetType";
import { SecondLayerMenu } from "./SecondLayerMenu";

const useStyles = makeStyles((theme: Theme) => ({
    menuItem: {
        minWidth: "260px",
    },
    iconFolder: {
        color: "rgb(143, 143, 143)",
    },
    iconSeries: {
        color: "rgb(118, 39, 187)",
    },
    selectedAsset: {
        backgroundColor: "rgb(238, 238, 238)"
    },
    seeMoreChevron: {
        borderRadius: "50%",
        color: "rgb(175, 175, 175)",
        display: "inherit",
        "&:hover": {
            backgroundColor: "rgb(209, 209, 209)",
            color: "rgb(77, 77, 77)",
        }
    },
    moreListChevron: {
        borderRadius: "50%",
        color: "rgb(175, 175, 175)",
        display: "inherit",
        "&:hover": {
            color: "rgb(77, 77, 77)",
        }
    },
    moreMenuItem: {
        "&:hover": {
            "& svg": {
                color: "rgb(77, 77, 77)",
            },
            "& div p:first-of-type": {
                borderRight: "1px solid rgb(209, 209, 209)",
            }
        }
    },
    listDivider: {
        borderRight: "1px solid rgb(240, 240, 240)",
        width: "172px"
    },
}));

interface Props {
    anchorElement: HTMLElement;
    onClose: () => void;
}

export function FirstLayerMenu(props:Props){
    const { onlineMode } = useOfflineAssets();
    const { anchorElement } = props;
    const classes = useStyles();
    const [addFolder, setaddFolder] = useState<"series" | "folder" | null>(null);
    const [selectedMenuCategory, setSelectedMenuCategory] = useState<AssetCategoryType>();
    
    const [showActionMenu, setShowActionMenu] = useState<HTMLElement>();
    const [showCategoryMenu, setShowCategoryMenu] = useState<HTMLElement>();

    const handleOpenSecondLayer = (element:HTMLElement, selectedCategory:AssetCategory) => {
        const categoryType = AssetCategoryTypes.find(a => a.category === selectedCategory);
        setSelectedMenuCategory(categoryType);
        setShowActionMenu(element);
    }

    return (
        <Popover
            id="add-menu"
            anchorEl={anchorElement}
            open={Boolean(anchorElement)}
            onClose={props.onClose}
            TransitionComponent={Fade}
        >
            <MenuItem
                disabled={!onlineMode}
                className={classes.menuItem}
                onClick={() => setaddFolder("folder")}
            >
                <ListItemIcon className={classes.iconFolder}>
                    <FolderIcon />
                </ListItemIcon>
                <ListItemText style={{paddingLeft: 16}} primary="Folder" />
            </MenuItem>
            <MenuItem
                disabled={!onlineMode}
                className={classes.menuItem}
                onClick={() => setaddFolder("series")}
            >
                <ListItemIcon className={classes.iconSeries}>
                    <SermonSeriesIcon />
                </ListItemIcon>
                <ListItemText style={{paddingLeft: 16}} primary="Series" />
            </MenuItem>
            <Divider />
            <MenuItem
                disabled={!onlineMode}
                onClick={(e) => handleOpenSecondLayer(e.currentTarget, AssetCategory.Manuscript)}
                className={classNames(classes.menuItem, classes.moreMenuItem, selectedMenuCategory && selectedMenuCategory.category === AssetCategory.Manuscript ? classes.selectedAsset : null)}
            >
                <ListItemText
                    inset
                    disableTypography
                    primary={
                        <Typography
                            variant="body1"
                            className={classes.listDivider}
                        >
                            Sermon Manuscript
                        </Typography>
                    }
                />
                <ListItemIcon>
                    <ChevronRightIcon fontSize="default" className={classes.seeMoreChevron} />
                </ListItemIcon>
            </MenuItem>
            <MenuItem
                disabled={!onlineMode}
                onClick={(e) => handleOpenSecondLayer(e.currentTarget, AssetCategory.Outline)}
                className={classNames(classes.menuItem, classes.moreMenuItem, selectedMenuCategory && selectedMenuCategory.category === AssetCategory.Outline ? classes.selectedAsset : null)}
            >
                <ListItemText
                    inset
                    disableTypography
                    primary={
                        <Typography
                            variant="body1"
                            className={classes.listDivider}
                        >
                            Sermon Outline
                        </Typography>
                    }
                />
                <ListItemIcon>
                    <ChevronRightIcon fontSize="default" className={classes.seeMoreChevron} />
                </ListItemIcon>
            </MenuItem>
            <MenuItem
                disabled={!onlineMode}
                className={classNames(classes.menuItem, showCategoryMenu ? classes.selectedAsset : null)}
                onClick={(e) => setShowCategoryMenu(e.currentTarget)}
            >
                <ListItemText
                    inset
                    disableTypography
                    primary={
                        <Typography
                            variant="body1"
                        >
                            More
                        </Typography>
                    }
                />
                <ListItemIcon>
                    <ChevronRightIcon fontSize="default" className={classes.moreListChevron} />
                </ListItemIcon>
            </MenuItem>
            {showCategoryMenu &&
                <Popover
                    open={showCategoryMenu !== undefined}
                    anchorEl={showCategoryMenu}
                    onClose={() => setShowCategoryMenu(undefined)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}                >
                    <List component="nav" style={{ marginTop: "8px", marginBottom: "8px" }}>
                        {AssetCategoryTypes.filter(a => a.name !== "Manuscript" && a.name !== "Outline").map((asset) => {
                            return (
                                <MenuItem
                                    key={asset.id}
                                    disabled={!onlineMode}
                                    onClick={(e) => handleOpenSecondLayer(e.currentTarget, asset.category)}
                                    className={classNames(classes.moreMenuItem, selectedMenuCategory && selectedMenuCategory.category === asset.category ? classes.selectedAsset : null)}
                                >
                                    <ListItemText
                                        disableTypography
                                        primary={
                                            <Typography
                                                variant="body1"
                                                style={{marginRight: 16}}
                                            >
                                                {asset.displayName}
                                            </Typography>
                                        }
                                    />
                                    <div>
                                        <ChevronRightIcon fontSize="default" className={classes.seeMoreChevron} />
                                    </div>
                                </MenuItem>
                            );
                        })}
                    </List>
                </Popover>
            }
            { addFolder && <CreateFolderDialog selectedFolderType={addFolder} onClose={props.onClose}/>}
            { showActionMenu && selectedMenuCategory && 
                <Popover
                    open={true}
                    anchorEl={showActionMenu}
                    onClose={() => setShowActionMenu(undefined)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <SecondLayerMenu onCloseAll={props.onClose} selectedMenuCategoryType={selectedMenuCategory} />
                </Popover>
            }
        </Popover>
    )
}